import { READ_AUDIT_LOG } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

/**
 * The Reports group (DSH-10): Sales, Purchase, Inventory, Financial
 * Reports (P&L, Balance Sheet) — and DSH-09 the Audit Log, "the fifth and
 * last entry in the Reports group" (DSH-09 §3).
 *
 * EVERY TAB HERE IS CONDITIONAL, and none of them on `reports.view`. That
 * permission is the GROUP's, one rung coarser than any screen inside it:
 * `reports.view` is what the controller demands on all nine report routes,
 * and each center then demands its own document area inside the service
 * before it computes a figure (BR-DSH-10-13 — "a report must never show a
 * figure the signed-in user has no permission to read"). The tab strip is the
 * menu half of that same sentence, so it has to ask the same five questions
 * the server asks and not the one the group asks.
 *
 * The five, each read off the line that enforces it rather than off the tab's
 * name — a tab hidden on the wrong permission locks out somebody entitled to
 * the screen, which is worse than the 403 it replaces:
 *
 *   Sales      `sales.view`       reports.service.ts `sales()`
 *   Purchases  `purchasing.view`  reports.service.ts `purchases()` — AND
 *                                 `priceTrend()`, the drift card the same
 *                                 screen calls as a second endpoint, on the
 *                                 same grant
 *   Inventory  `inventory.view`   reports.service.ts `inventory()`
 *   Financial  `accounting.view`  financial-statements.service.ts `run()`
 *   Statement  `crm.view`         reports.service.ts `partnerStatement()` —
 *                                 CRM-03, the partner statement as a centre;
 *                                 the area is 16.2.2 row 5's ("statements")
 *   Audit Log  READ_AUDIT_LOG     its own grant in the admin area
 *
 * The Audit Log tab was the first conditional one, on a permission that is
 * not `reports.view`: the log's read is its own grant in the admin area
 * (BR-DSH-09-13, BR-PERM-52), so a person with the whole Reports group may
 * still not have it.
 *
 * ABSENT rather than greyed, which is the reading BR-PERM-18 and BR-PERM-19
 * leave open for a tab strip — the spec has no rule for one (acceptance
 * finding D-8), and this build has answered it the same way twice already:
 * `hr-tabs.tsx` hides the payroll tab. A tab is navigation, and BR-PERM-19
 * governs navigation.
 *
 * THE PAYROLL PRECEDENT DOES NOT CARRY ITS SECOND HALF OVER, and this comment
 * used to say it did — "typing the address still lands on 16.7's page". True
 * of payroll, because `/employees/payroll` has its own entry in
 * `route-permissions.ts` naming the `payroll` area. It is NOT true of any tab
 * here: no report route has such an entry, so all four inherit the Reports
 * group's area and open for anybody holding `reports.view`. See the last
 * section of this comment — the correction is the whole reason it is there.
 *
 * FINANCIAL REPORTS IS THE SECOND CONDITIONAL TAB, for the same reason and on
 * a different permission. BR-DSH-08-18: "A user without general-ledger read
 * permission must not see this screen in the menu", and DSH-08 §2 names that
 * permission — "read permission on the general ledger, THE SAME PERMISSION
 * THAT GATES DSH-01", which is `accounting.view` and is what
 * `FinancialStatementsService.run` demands before it computes a figure.
 *
 * The Reports GROUP is gated on `reports.view`, one rung coarser, so a
 * bookkeeper holding the reports area and nothing else used to see this tab,
 * click it, and be handed a 403 by a screen the rule says should not have been
 * offered. §2's own access table is blunt about who this screen is for:
 * "Salesperson, warehouse staff — No access. This screen shows whole-company
 * financial position." The server was already right; only the door was wrong.
 *
 * SALES AND PURCHASES ARE THE SAME DEFECT WITH THEIR OWN SPEC ROWS, and both
 * rows say it in the same words. ERR-DSH-05-05: "Opened the screen without
 * sales permission — the screen does not appear in the menu. Ask an
 * administrator." ERR-DSH-06-04 says exactly that for purchase permission.
 * Both were unconditional here, so the row's stated behaviour — the ABSENCE —
 * existed nowhere, and what a reader actually met was the tab, the click and
 * the 403.
 *
 * INVENTORY IS GATED ON THE SAME REASONING AND CITES NO ERR ROW, deliberately.
 * DSH-07 has no "without inventory permission" row and no rule of its own
 * about its menu; its only permission rules concern COST visibility and point
 * the other way — that screen is SHOWN to a reader without the cost grant,
 * with the cost, value, turnover and ABC content removed and a note saying so.
 * Citing one of those here would name a rule that describes the opposite
 * behaviour. What governs the Inventory tab is what governs the strip:
 * BR-PERM-19, "a menu section a role cannot view must be absent, not greyed",
 * over a server that demands `inventory.view` before it returns a row.
 *
 * ── THIS STRIP IS NOT A GATE, AND THE ROUTE HALF IS COARSER THAN IT IS ─────
 *
 * BR-PERM-51: "Navigation filtering is a display convenience… A route must
 * never rely on its menu entry being hidden." The real refusals are the
 * service demands listed above, and they are unchanged by anything here.
 *
 * Worth stating precisely, because a green test about this file is easy to
 * read as more than it is: `RequirePermission` resolves these four routes
 * through the Reports nav group, whose requirement is the AREA `reports` —
 * so a reader holding `reports.view` and nothing else no longer sees the
 * tabs, and typing the address still renders the screen and collects the
 * server's 403 inside it. Menu granularity is per-permission; route
 * granularity is per-area. Closing that gap is a change to
 * `app/route-permissions.ts`, which this file cannot make.
 */
export function ReportsTabs() {
  const { t } = useTranslation();
  const canReadSales = useCan('sales.view');
  const canReadPurchasing = useCan('purchasing.view');
  const canReadInventory = useCan('inventory.view');
  const canReadLedger = useCan('accounting.view');
  const canReadStatements = useCan('crm.view');
  const canReadAudit = useCan(READ_AUDIT_LOG);
  const tabs = [
    // ERR-DSH-05-05 — the sales center's own row, and the menu half of it.
    ...(canReadSales ? [{ to: '/reports', end: true, key: 'reports.tabSales' } as const] : []),
    // ERR-DSH-06-04 — the purchase center's row, verbatim the same sentence.
    // One grant covers both of this screen's endpoints, the report and the
    // price-drift card, because the service demands `purchasing.view` at each.
    ...(canReadPurchasing
      ? [{ to: '/reports/purchases', end: false, key: 'reports.tabPurchases' } as const]
      : []),
    ...(canReadInventory
      ? [{ to: '/reports/inventory', end: false, key: 'reports.tabInventory' } as const]
      : []),
    // ERR-DSH-08-08 / ERR-DSH-08-15 are the two halves of getting this wrong:
    // "the screen is not in the menu" is what a reader without the permission
    // should meet, and "a user without ledger permission reached the screen"
    // is what they met instead.
    ...(canReadLedger ? [{ to: '/reports/financial', end: false, key: 'reports.tabFinancial' } as const] : []),
    // CRM-03 — the statement centre reads the partner ledgers, so it follows
    // the crm area's read, which is what the service demands.
    ...(canReadStatements
      ? [{ to: '/reports/statement', end: false, key: 'reports.tabStatement' } as const]
      : []),
    ...(canReadAudit ? [{ to: '/reports/audit', end: false, key: 'reports.tabAudit' } as const] : []),
  ];
  // A reader holding the group and no center now has nothing to draw. An empty
  // strip is a bordered rule across the page with no content under it, which
  // reads as a broken screen rather than as an absence — BR-PERM-19 asks for
  // absent, so the strip goes too rather than becoming a line.
  if (tabs.length === 0) return null;
  return (
    <nav className="no-print flex flex-wrap gap-1 border-b pb-px" aria-label={t('shell.navReports')}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
              isActive && 'border-b-2 border-primary text-foreground',
            )
          }
        >
          {t(tab.key)}
        </NavLink>
      ))}
    </nav>
  );
}
